import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, FileSearch } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { PageHero, CTABand } from "../components/PageParts";
import SectionHeading from "../components/SectionHeading";
import ProcessFlow from "../components/ProcessFlow";
import RiskNote from "../components/RiskNote";
import { FAQ, IllustrativeChartCard } from "../components/Cards";
import { FAQS, EXTERNAL_LINKS } from "../config/site";

const STEPS = [
  {
    title: "Review the vault terms",
    desc: "Each vault publishes its objectives, methodology, custody arrangement and participation terms.",
  },
  {
    title: "Participate via the custodial arrangement",
    desc: "Participating assets are committed to the vault and held with the exchange or host platform under its custody structure.",
  },
  {
    title: "Trading runs per methodology",
    desc: "The vault's mandate executes according to its stated trading methodology and risk framework.",
  },
  {
    title: "Redeem per the vault's windows",
    desc: "Withdrawals follow the vault's published redemption windows, notice periods and conditions.",
  },
];

const VAULTS = [
  {
    id: "vault-01",
    name: "Kalki Flagship Vault",
    focus: "Major pairs · Systematic long/short",
    description:
      "The flagship custodial vault, trading liquid major pairs under a documented systematic mandate with defined risk limits.",
    custody: "Exchange-hosted custody (specified in participation terms)",
    performance: "Verified historical performance will be published here when available.",
    fees: "Fee schedule published when confirmed with the operating provider.",
  },
  {
    id: "vault-02",
    name: "Kalki Structured Vault",
    focus: "Multi-strategy · Defined mandate",
    description:
      "A multi-strategy custodial vault combining directional and hedged sub-approaches within a single governed mandate.",
    custody: "Exchange-hosted custody (specified in participation terms)",
    performance: "Verified historical performance will be published here when available.",
    fees: "Fee schedule published when confirmed with the operating provider.",
  },
];

export default function VaultTrading() {
  return (
    <>
      <Seo
        title="Vault Trading — Custodial & Exchange-Hosted"
        path="/vault-trading"
        description="Custodial trading vaults with a clearly explained operating model, custody arrangement, trading methodology, verified performance where available, and full risk disclosures."
      />

      <PageHero
        eyebrow="Offering 03 · Vault Trading"
        titleLines={["Custodial vaults,", "clearly structured."]}
        intro="Custodial trading vault opportunities presented with a clear explanation of the operating model, custody arrangements, trading methodology and associated risks. This model is custodial — your assets are held under the vault's custody structure, not in your own account."
        badges={["Custodial", "Exchange-hosted"]}
        image="https://images.pexels.com/photos/14866072/pexels-photo-14866072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
        imageAlt="A bank vault door inside a classical banking hall"
      />

      {/* Overview + custody */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14">
            <SectionHeading
              eyebrow="The Custodial Model"
              title={<>What “custodial” means here — precisely</>}
              intro="Vault Trading is a custodial arrangement. That word changes everything about how risk works, so we define it directly rather than gloss over it."
            />
            <Reveal delay={0.15} className="flex flex-col gap-5">
              <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
                When you participate in a vault, your assets are held with the exchange or host
                platform under the vault's custody arrangement. You rely on that venue's custody,
                solvency and operational controls for as long as you remain a participant.
              </p>
              <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
                This is a fundamental difference from our non-custodial offerings (Master Trading
                and Strategy Trading), where assets never leave your account. We state the
                distinction on every page, because it should drive your decision.
              </p>
              <div className="rounded-2xl border border-crimson-400/25 bg-crimson-400/[0.05] p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-crimson-400 mb-2">
                  Custody disclosure
                </p>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Participating assets are held with the exchange or host platform under the
                  vault's custodial structure. Counterparty and custody risk applies, including in
                  insolvency scenarios of the custodian.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="mt-20">
            <ProcessFlow idPrefix="vault" steps={STEPS} />
          </div>
        </div>
      </section>

      {/* Vaults */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10 bg-ink-800/40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="The Vaults"
            title={<>Objectives, methodology, disclosed terms.</>}
            intro="Performance figures appear only when verified against the host platform's records. Terms that depend on provider confirmation are labelled as unconfirmed rather than estimated."
          />
          <Stagger className="mt-14 grid gap-6 lg:grid-cols-2">
            {VAULTS.map((v) => (
              <StaggerItem key={v.id} className="h-full">
                <article
                  data-testid={`vault-card-${v.id}`}
                  className="glass rounded-3xl p-8 h-full flex flex-col hover:border-gold-400/35 transition-colors duration-500"
                >
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <div>
                      <h3 className="font-display text-2xl font-bold tracking-tight text-ink-50 mb-1">
                        {v.name}
                      </h3>
                      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-gold-400/70">
                        {v.focus}
                      </p>
                    </div>
                    <Lock size={16} className="text-gold-400/60 shrink-0 mt-1.5" />
                  </div>
                  <p className="text-sm text-zinc-400 leading-relaxed mb-6">{v.description}</p>
                  <div className="flex flex-col gap-4 mb-8">
                    {[
                      ["Trading methodology", "Defined in the vault's mandate document, with risk limits and review cadence."],
                      ["Custody", v.custody],
                      ["Verified performance", v.performance],
                      ["Fees", v.fees],
                      ["Withdrawals", "Per the vault's published redemption windows and conditions."],
                    ].map(([label, text]) => (
                      <div key={label} className="border-t border-white/[0.06] pt-3.5">
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-gold-400/70 mb-1">
                          {label}
                        </p>
                        <p className="text-sm text-zinc-300 leading-relaxed">{text}</p>
                      </div>
                    ))}
                  </div>
                  <a
                    href={EXTERNAL_LINKS.vaultTrading}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`vault-link-${v.id}`}
                    className="mt-auto inline-flex items-center justify-center gap-2 rounded-full border border-gold-400/35 bg-gold-400/[0.05] px-5 py-2.5 text-sm font-semibold text-gold-300 hover:bg-gold-400/[0.1] hover:border-gold-400/60 transition-all duration-300"
                  >
                    Vault participation details
                    <ArrowUpRight size={14} />
                  </a>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <p className="mt-8 text-xs text-zinc-600 font-mono">
              External vault destinations are placeholders, configured in the site's content
              configuration file.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Verification + chart */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <SectionHeading
              eyebrow="Verified Reporting"
              title={<>Performance, published the right way</>}
              intro="When vault performance is verified against the host platform's records, it is published with its reporting period, maximum drawdown, fees and expenses, data source and last-updated date — and labelled live, audited or independently verified as applicable."
            />
            <Reveal delay={0.15}>
              <div className="glass-gold rounded-3xl p-7 flex items-start gap-4">
                <FileSearch size={20} className="text-gold-400 shrink-0 mt-1" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-400/80 mb-2">
                    Verification status
                  </p>
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    No vault performance figures are shown on this page yet, because no verified
                    report is available for publication at this time. This placeholder will be
                    replaced with verified data — never estimated.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="mt-16">
            <IllustrativeChartCard testid="vault-illustrative-chart" />
          </div>
        </div>
      </section>

      {/* Risk + FAQ */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <RiskNote
            lines={[
              "Vault Trading is custodial: participating assets are held with the exchange or host platform under the vault's custody arrangement, and participants bear that custodian's counterparty and operational risk.",
              "Withdrawals follow the vault's redemption windows and may be restricted during defined periods. Market risk, venue risk and liquidity risk all apply.",
            ]}
          />
          <div className="mt-16">
            <SectionHeading eyebrow="FAQ" title={<>Frequently asked questions</>} />
            <div className="mt-10">
              <FAQ items={FAQS.vault} idPrefix="vault-faq" />
            </div>
          </div>
        </div>
      </section>

      <CTABand
        testid="vault-cta"
        title="Discover Vault Trading in full."
        subtitle="The configured destination carries participation documentation — or contact the team with any question first."
        primary={{ label: "Discover Vault Trading", to: EXTERNAL_LINKS.vaultTrading, external: true }}
        secondary={{ label: "Contact Our Team", to: "/contact" }}
      />
    </>
  );
}